import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { createEffect, sample } from "effector";
import { IPromptsLibraryContract } from ".";
import { IAppRouteUnitsContract } from "@features/app/route-units";
import { IPromptsLibraryUIContract } from "../ui";
import {
    promptLibraryBannerClicked,
    promptLibraryCategoryClicked,
    promptLibraryCopyClicked,
    promptLibraryOpened,
    promptLibraryTryClicked,
    promptLibraryUnlockClicked
} from "@features/shared/events";

const trackPromptLibraryOpenedFx = createEffect(() => {
    promptLibraryOpened();
});

const trackPromptLibraryUnlockClickedFx = createEffect(
    (data: {
        placement: "button" | "prompt" | "banner";
        promptId?: number;
        promptTitle?: string;
        promptText?: string;
        category?: string;
        categoryId?: number;
    }) => {
        const { placement, promptId, promptTitle, promptText, category, categoryId } = data;
        if (placement === "prompt" && promptId && promptTitle && promptText) {
            promptLibraryUnlockClicked({
                placement,
                promptId,
                promptTitle,
                promptText
            });
        } else if ((placement === "button" || placement === "banner") && category && categoryId) {
            promptLibraryUnlockClicked({
                placement,
                category,
                categoryId
            });
        } else {
            promptLibraryUnlockClicked({ placement });
        }
    }
);

const trackPromptLibraryBannerClickedFx = createEffect((placement: "home" | "profile" | "rail") => {
    promptLibraryBannerClicked({ placement });
});

const trackPromptLibraryCategoryClickedFx = createEffect(
    ({ category, categoryId, type }: { category: string; categoryId: number; type: "top" | "sub" }) => {
        promptLibraryCategoryClicked({ category, categoryId, type });
    }
);

const trackPromptLibraryTryClickedFx = createEffect(
    ({ promptText, promptTitle, id }: { promptText: string; promptTitle: string; id: number }) => {
        promptLibraryTryClicked({ promptText, promptTitle, id });
    }
);

const trackPromptLibraryCopyClickedFx = createEffect(
    ({ promptText, promptTitle, id }: { promptText: string; promptTitle: string; id: number }) => {
        promptLibraryCopyClicked({ promptText, promptTitle, id });
    }
);

const contract = new ModelContract({}, {}, {}, {});

export type IPromptsLibraryAnalyticsContract = typeof contract;

export class PromptsLibraryAnalyticsModule extends ModuleDescription<IPromptsLibraryAnalyticsContract> {
    constructor(
        public promptsLibrary: IPromptsLibraryContract,
        public promptsLibraryUI: IPromptsLibraryUIContract,
        public routes: IAppRouteUnitsContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run() {
        sample({
            clock: this.routes.stores.promptsLibrary.opened,
            target: trackPromptLibraryOpenedFx
        });

        sample({
            clock: this.promptsLibraryUI.events.unlockButtonClicked,
            fn: (s) => s,
            target: trackPromptLibraryUnlockClickedFx
        });

        sample({
            clock: this.promptsLibraryUI.events.packClicked,
            fn: (s) => s.placement,
            target: trackPromptLibraryBannerClickedFx
        });

        sample({
            clock: [this.promptsLibraryUI.events.categoryClicked, this.promptsLibraryUI.events.subcategoryClicked],
            fn: (s) => ({
                category: s.category,
                categoryId: s.categoryId,
                type: s.type
            }),
            target: trackPromptLibraryCategoryClickedFx
        });

        sample({
            clock: this.promptsLibraryUI.events.tryButtonClicked,
            fn: (prompt) => ({
                promptText: prompt.text,
                promptTitle: prompt.title,
                id: prompt.id
            }),
            target: trackPromptLibraryTryClickedFx
        });

        sample({
            clock: this.promptsLibraryUI.events.copyButtonClicked,
            fn: (prompt) => ({
                promptText: prompt.text,
                promptTitle: prompt.title,
                id: prompt.id
            }),
            target: trackPromptLibraryCopyClickedFx
        });
    }
}
